import { Link } from "react-router";
import { ArrowRight, BellRing, Calendar, Clock, GripVertical, User } from "lucide-react";
import { Body, Caption, VBadge, VHoverCard, cn } from "@/components/ui";
import type { CameraTask, TaskStatus } from "@/domains/tasks/api/types";
import { TaskStatusBadge } from "@/domains/tasks/components/taskStatusBadge";
import {
  TASK_CATEGORY_COLOR,
  TASK_CATEGORY_LABELS,
} from "@/domains/tasks/components/taskCategoryLabel";
import {
  getTaskTemplateIcon,
  getTaskTemplateLabel,
} from "@/domains/tasks/components/taskTemplateIcon";

type TaskCardProps = {
  task: CameraTask;
  draggable?: boolean;
  compact?: boolean;
  className?: string;
};

const STATUS_DOT_COLOR: Record<TaskStatus, string> = {
  todo: "bg-neutral-400",
  inProgress: "bg-info-500",
  done: "bg-success-500",
  blocked: "bg-error-500",
};

const STATUS_TEXT_COLOR: Record<TaskStatus, string> = {
  todo: "text-neutral-700",
  inProgress: "text-info-700",
  done: "text-success-700",
  blocked: "text-error-700",
};

const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: "Te doen",
  inProgress: "Bezig",
  done: "Klaar",
  blocked: "Geblokkeerd",
};

const ASSIGNEE_KIND_LABEL = {
  internal: "Intern",
  partner: "Externe partner",
  requester: "Aanvrager",
} as const;

function TaskDetailPanel({
  task,
  templateLabel,
  TemplateIcon,
}: {
  task: CameraTask;
  templateLabel: string;
  TemplateIcon: ReturnType<typeof getTaskTemplateIcon>;
}): React.JSX.Element {
  const shortCaseId = task.caseId.replace("case-", "");
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex size-6 items-center justify-center rounded bg-muted text-muted-foreground">
            <TemplateIcon className="size-3.5" aria-hidden />
          </span>
          <Caption tone="muted" className="uppercase tracking-wide">
            {templateLabel}
          </Caption>
        </span>
        <TaskStatusBadge status={task.status} />
      </div>

      <Body
        weight="semibold"
        className={cn(
          "leading-snug text-foreground",
          task.status === "done" && "line-through",
        )}
      >
        {task.title}
      </Body>

      {task.description ? (
        <Body tone="muted" className="leading-relaxed text-sm">
          {task.description}
        </Body>
      ) : null}

      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 border-t border-border pt-3">
        <div>
          <Caption as="p" tone="muted">
            Categorie
          </Caption>
          <VBadge color="neutral" className="mt-1">
            {TASK_CATEGORY_LABELS[task.category]}
          </VBadge>
        </div>
        <div>
          <Caption as="p" tone="muted">
            Deadline
          </Caption>
          <Body className="mt-0.5 inline-flex items-center gap-1 text-sm">
            <Calendar className="size-3.5 text-muted-foreground" aria-hidden />
            {task.dueDate}
          </Body>
        </div>
        {task.assignee ? (
          <div className="col-span-2">
            <Caption as="p" tone="muted">
              Toegewezen aan
            </Caption>
            <Body className="mt-0.5 inline-flex items-center gap-1 text-sm">
              <User className="size-3.5 text-muted-foreground" aria-hidden />
              {task.assignee.name}
              <span className="text-muted-foreground">
                · {ASSIGNEE_KIND_LABEL[task.assignee.kind]}
              </span>
            </Body>
          </div>
        ) : (
          <div className="col-span-2">
            <Caption as="p" tone="muted">
              Toegewezen aan
            </Caption>
            <Body className="mt-0.5 italic text-sm text-muted-foreground">
              Niet toegewezen
            </Body>
          </div>
        )}
        {task.reminderDate ? (
          <div className="col-span-2">
            <Caption as="p" tone="muted">
              Reminder
            </Caption>
            <Body className="mt-0.5 inline-flex items-center gap-1 text-sm">
              <BellRing className="size-3.5 text-warning-600" aria-hidden />
              {task.reminderDate}
            </Body>
          </div>
        ) : null}
      </div>

      <Link
        to={`/cases/${task.caseId}`}
        className="group/link inline-flex items-center gap-1 border-t border-border pt-3 text-xs font-semibold text-primary-700 hover:text-primary-800"
      >
        Bekijk dossier #{shortCaseId}
        <ArrowRight
          className="size-3.5 transition-transform group-hover/link:translate-x-0.5"
          aria-hidden
        />
      </Link>
    </div>
  );
}

export function TaskCard({
  task,
  draggable,
  compact,
  className,
}: TaskCardProps): React.JSX.Element {
  const TemplateIcon = getTaskTemplateIcon(task.templateId);
  const templateLabel = getTaskTemplateLabel(task.templateId);

  const detailPanel = (
    <TaskDetailPanel
      task={task}
      templateLabel={templateLabel}
      TemplateIcon={TemplateIcon}
    />
  );

  if (compact) {
    const shortCaseId = task.caseId.replace("case-", "");
    return (
      <VHoverCard trigger={
        <div
          className={cn(
            "flex flex-col gap-1 rounded-md border bg-card px-2 py-2 shadow-sm transition-shadow hover:shadow-md",
            TASK_CATEGORY_COLOR[task.category],
            task.status === "done" && "opacity-70",
            className,
          )}
        >
          <div className="flex items-start gap-1.5">
            <TemplateIcon
              className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
              aria-label={templateLabel}
            />
            <Body
              weight="semibold"
              className={cn(
                "line-clamp-2 text-xs leading-snug text-foreground",
                task.status === "done" && "line-through",
              )}
            >
              {task.title}
            </Body>
          </div>

          {task.description ? (
            <Caption tone="muted" as="p" className="line-clamp-2 text-[11px] leading-snug">
              {task.description}
            </Caption>
          ) : null}

          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
            {task.assignee ? (
              <span className="inline-flex min-w-0 items-center gap-1">
                <User className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{task.assignee.name}</span>
              </span>
            ) : (
              <span className="italic opacity-60">Niet toegewezen</span>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-1 text-[11px]">
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <span
                className={cn("size-2 shrink-0 rounded-full", STATUS_DOT_COLOR[task.status])}
                aria-hidden
              />
              <span
                className={cn(
                  "truncate font-medium",
                  STATUS_TEXT_COLOR[task.status],
                )}
              >
                {STATUS_LABEL[task.status]}
              </span>
            </span>
            <Link
              to={`/cases/${task.caseId}`}
              className="shrink-0 font-medium text-primary-700 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              #{shortCaseId}
            </Link>
          </div>
        </div>
      }>
        {detailPanel}
      </VHoverCard>
    );
  }

  return (
    <VHoverCard trigger={
      <div
        className={cn(
          "flex items-start gap-2 rounded-md border bg-card px-2.5 py-2 shadow-sm transition-shadow hover:shadow-md",
          TASK_CATEGORY_COLOR[task.category],
          task.status === "done" && "opacity-70",
          className,
        )}
      >
        {draggable ? (
          <GripVertical className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : null}
        <span
          className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
        >
          <TemplateIcon className="size-4" aria-label={templateLabel} />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <Body
            weight="semibold"
            className={cn("text-foreground", task.status === "done" && "line-through")}
          >
            {task.title}
          </Body>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Caption tone="muted" className="inline-flex items-center gap-1">
              <Clock className="size-3" aria-hidden />
              {task.dueDate}
            </Caption>
            {task.assignee ? (
              <Caption tone="muted" className="inline-flex items-center gap-1">
                <User className="size-3" aria-hidden />
                {task.assignee.name}
              </Caption>
            ) : null}
            <Caption tone="muted">{TASK_CATEGORY_LABELS[task.category]}</Caption>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <TaskStatusBadge status={task.status} />
            <Link
              to={`/cases/${task.caseId}`}
              className="text-xs font-medium text-primary-700 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              #{task.caseId.replace("case-", "")}
            </Link>
          </div>
        </div>
      </div>
    }>
      {detailPanel}
    </VHoverCard>
  );
}
